import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useImmerReducer } from "use-immer";
import { useImmer } from "use-immer";
import SimpleReactValidator from "simple-react-validator";
import {
  DEFAULT_PAGINATION,
  errorMessage,
  successMessage,
} from "../../../utils";
import { AppContext } from "../../../store";
import { useDropdownApis, getProductsList } from "../../../shared";
import { useDebounce } from "use-debounce";
import qs from "qs";
import {
  createSegment,
  getSegmentationBoot,
  getSegmentById,
  searchSegmentCustomers,
  updateSegment,
} from "../api";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

let _ruleCounter = 0;
const nextRuleId = () => `rule-${++_ruleCounter}`;

/** Converts "snake_case_column" → "Snake Case Column" */
export const formatColumnLabel = (rule, allCriteria) => {
  return (
    allCriteria[rule.table].find((f) => f.column === rule.column)?.label || "--"
  );
};

/** Given bootData.criterias, look up the type for a given table+column */
export const lookupColumnType = (criterias, table, column) => {
  const fields = criterias[table] ?? [];
  return fields.find((f) => f.column === column)?.type ?? "string";
};

// operators that need no value input
export const NO_VALUE_OPERATORS = new Set([
  "IS EMPTY",
  "IS NOT EMPTY",
  "IS NULL",
  "IS NOT NULL",
]);

/** Strips internal fields and converts types for API payloads */
const formatRulesForApi = (rules) =>
  rules.map(({ table, column, operator, value, type }) => ({
    table,
    column,
    operator,
    value: type === "boolean" && value !== "" ? Number(value) : value,
  }));

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

const ACTION = {
  SET_BOOT_LOADING: "SET_BOOT_LOADING",
  SET_BOOT_DATA: "SET_BOOT_DATA",
  SET_DETAIL_LOADING: "SET_DETAIL_LOADING",
  SET_FORM_DATA: "SET_FORM_DATA",
  SET_FIELD: "SET_FIELD",
  SET_LOGICAL_OPERATOR: "SET_LOGICAL_OPERATOR",
  ADD_RULE: "ADD_RULE",
  UPDATE_RULE: "UPDATE_RULE",
  REPLACE_RULE: "REPLACE_RULE",
  REMOVE_RULE: "REMOVE_RULE",
  SET_PREVIEW_LOADING: "SET_PREVIEW_LOADING",
  SET_PREVIEW: "SET_PREVIEW",
  SET_SAVING: "SET_SAVING",
  SET_DIALOG_OPEN: "SET_DIALOG_OPEN",
  SET_DIALOG_TAB: "SET_DIALOG_TAB",
  SET_EDITING_RULE: "SET_EDITING_RULE",
  CLEAR_EDITING_RULE: "CLEAR_EDITING_RULE",
};

const reducer = (draft, action) => {
  switch (action.type) {
    case ACTION.SET_BOOT_LOADING:
      draft.bootLoading = action.payload;
      break;
    case ACTION.SET_BOOT_DATA:
      draft.bootData = action.payload;
      break;
    case ACTION.SET_DETAIL_LOADING:
      draft.isDetailLoading = action.payload;
      break;
    case ACTION.SET_FORM_DATA:
      draft.formData = action.payload;
      break;
    case ACTION.SET_FIELD:
      draft.formData[action.payload.name] = action.payload.value;
      break;
    case ACTION.SET_LOGICAL_OPERATOR:
      draft.formData.logical_operator = action.payload;
      draft.previewCustomers = [];
      draft.previewPagination = DEFAULT_PAGINATION;
      draft.hasSearched = false;
      break;
    case ACTION.ADD_RULE:
      draft.formData.rules.push(action.payload);
      draft.previewCustomers = [];
      draft.previewPagination = DEFAULT_PAGINATION;
      draft.hasSearched = false;
      break;
    case ACTION.UPDATE_RULE: {
      const rule = draft.formData.rules[action.payload.index];
      rule[action.payload.field] = action.payload.value;
      // When operator changes to a no-value op, clear the value
      if (
        action.payload.field === "operator" &&
        NO_VALUE_OPERATORS.has(action.payload.value)
      ) {
        rule.value = "";
      }
      // When operator changes FROM between to something else, simplify value
      if (
        action.payload.field === "operator" &&
        action.payload.value !== "BETWEEN"
      ) {
        if (Array.isArray(rule.value)) {
          rule.value = "";
        }
      }
      // When switching to BETWEEN, initialise as two-element array
      if (
        action.payload.field === "operator" &&
        action.payload.value === "BETWEEN"
      ) {
        rule.value = ["", ""];
      }
      draft.previewCustomers = [];
      draft.previewPagination = DEFAULT_PAGINATION;
      draft.hasSearched = false;
      break;
    }
    case ACTION.REPLACE_RULE: {
      const { index, operator, value } = action.payload;
      const r = draft.formData.rules[index];
      r.operator = operator;
      r.value = value;
      if (action.payload.list) {
        r.list = action.payload.list;
      }
      draft.previewCustomers = [];
      draft.previewPagination = DEFAULT_PAGINATION;
      draft.hasSearched = false;
      break;
    }
    case ACTION.REMOVE_RULE:
      draft.formData.rules.splice(action.payload, 1);
      draft.previewCustomers = [];
      draft.previewPagination = DEFAULT_PAGINATION;
      draft.hasSearched = false;
      break;
    case ACTION.SET_PREVIEW_LOADING:
      draft.previewLoading = action.payload;
      break;
    case ACTION.SET_PREVIEW:
      draft.previewCustomers = action.payload.customers;
      draft.previewPagination = action.payload.pagination;
      draft.hasSearched = true;
      break;
    case ACTION.SET_SAVING:
      draft.isSaving = action.payload;
      break;
    case ACTION.SET_DIALOG_OPEN:
      draft.criteriaDialogOpen = action.payload;
      break;
    case ACTION.SET_DIALOG_TAB:
      draft.criteriaDialogTab = action.payload;
      break;
    case ACTION.SET_EDITING_RULE:
      draft.editingRuleIndex = action.payload;
      draft.criteriaDialogOpen = true;
      break;
    case ACTION.CLEAR_EDITING_RULE:
      draft.editingRuleIndex = null;
      break;
    default:
      break;
  }
};

const initialState = {
  bootLoading: false,
  isDetailLoading: false,
  isSaving: false,
  bootData: { operators: {}, criterias: {} },
  formData: {
    name: "",
    description: "",
    logical_operator: "AND",
    rules: [],
  },
  previewLoading: false,
  previewCustomers: [],
  previewPagination: DEFAULT_PAGINATION,
  hasSearched: false,
  criteriaDialogOpen: false,
  criteriaDialogTab: 0,
  editingRuleIndex: null,
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useManageSegmentation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const { appState } = useContext(AppContext);
  const { globalData } = appState;
  const { storeList } = useDropdownApis({ isStoreList: true });

  // ---------------------------------------------------------------------------
  // Product autocomplete (self-contained, following useReviewAndRatings pattern)
  // ---------------------------------------------------------------------------
  const defaultProductState = {
    options: [],
    isLoading: false,
    pagination: {
      pageIndex: 1,
      pageSize: 10,
      pageCount: 0,
      total: 0,
      lastPage: 0,
      hasMorePages: true,
    },
    searchKey: null,
  };

  const [productState, setProductState] = useImmer(defaultProductState);
  const [debouncedProductSearch] = useDebounce(productState.searchKey, 1000);

  const getProductOptions = async (params, pagination, resetOptions) => {
    setProductState((draft) => {
      draft.isLoading = true;
    });
    try {
      const response = await getProductsList(
        {
          params: { ...params, product_types: ["bundle", "platter", "retail"] },
          paramsSerializer: (p) => qs.stringify(p),
        },
        pagination,
      );
      const { success, data } = response;
      if (success) {
        setProductState((draft) => {
          const base = resetOptions ? [] : draft.options;
          const newOptions = base.concat(data.products);
          const uniqueOptions = newOptions.filter(
            (item, index, array) =>
              index === array.findIndex((i) => i.id === item.id),
          );
          draft.options = uniqueOptions;
          draft.pagination = data.pagination;
        });
      } else {
        setProductState((draft) => {
          draft.isLoading = false;
        });
      }
    } catch (err) {
      setProductState((draft) => {
        draft.isLoading = false;
      });
      errorMessage(err?.response?.data?.message ?? "Failed to load products.");
    } finally {
      setProductState((draft) => {
        draft.isLoading = false;
      });
    }
  };
  const handleProductFetch = async (page) => {
    const { pageSize } = productState.pagination;
    const query = {};
    if (productState.searchKey) {
      Object.assign(query, { search: productState.searchKey });
    }
    await getProductOptions(query, {
      pageSize,
      pageIndex: page,
    });
  };

  const handleProductSearch = (event, inputValue, reason) => {
    if (!event) return;
    if (reason === "input") {
      setProductState((draft) => {
        draft.searchKey = inputValue;
      });
    }
  };

  const handleProductSearchClear = (keepItems) => {
    setProductState((draft) => {
      draft.searchKey = null;
      draft.options = keepItems ?? [];
      draft.pagination = defaultProductState.pagination;
    });
    // Fetch page 1 unfiltered — unique merge preserves keepItems
    getProductOptions(
      {},
      {
        pageSize: defaultProductState.pagination.pageSize,
        pageIndex: 1,
      },
    );
  };

  // Fetch products on debounced search change (skip initial mount)
  useEffect(() => {
    if (debouncedProductSearch === null) return;
    const { pageSize } = defaultProductState.pagination;
    const query = debouncedProductSearch
      ? { search: debouncedProductSearch }
      : {};
    getProductOptions(query, { pageSize, pageIndex: 1 }, true);
  }, [debouncedProductSearch]);

  const dropdownOptions = useMemo(
    () => ({
      payment_type: {
        options: globalData?.payment_types ?? [],
        valueKey: "label",
      },
      status: {
        options: globalData?.order_statues ?? [],
        valueKey: "label",
      },
      store_id: {
        options: (storeList ?? []).map((s) => ({ value: s.id, label: s.name })),
        valueKey: "value",
      },
      product_type: {
        options: globalData?.product?.type ?? [],
        valueKey: "value",
      },
      favourite_store_id: {
        options: (storeList ?? []).map((s) => ({ value: s.id, label: s.name })),
        valueKey: "value",
      },
    }),
    [
      globalData?.payment_types,
      globalData?.order_statues,
      globalData?.product?.type,
      storeList,
    ],
  );

  const asyncDropdownOptions = useMemo(
    () => ({
      product_id: {
        options: (productState.options ?? []).map((p) => ({
          value: p.id,
          label: p.name,
        })),
        valueKey: "value",
        hasMore: productState.pagination?.hasMorePages ?? false,
        isLoading: productState.isLoading,
        onInputChange: handleProductSearch,
        onLoadMore: handleProductFetch,
        onReset: handleProductSearchClear,
        setFieldState: setProductState,
        pagination: productState.pagination,
        searchKey: productState.searchKey,
      },
    }),
    [
      productState.options,
      productState.pagination,
      productState.isLoading,
      productState.searchKey,
    ],
  );

  const [, setForceUpdate] = useState(0);
  const [state, dispatch] = useImmerReducer(reducer, initialState);

  const validator = useRef(
    new SimpleReactValidator({
      element: (message) => message,
      autoForceUpdate: { forceUpdate: setForceUpdate },
    }),
  );

  // ---------------------------------------------------------------------------
  // Load existing segment for edit
  // ---------------------------------------------------------------------------

  const loadSegment = async (segmentId, bootData) => {
    dispatch({ type: ACTION.SET_DETAIL_LOADING, payload: true });
    try {
      const res = await getSegmentById(segmentId);
      if (res.success) {
        const seg = res.data;
        const rules = (seg.rules ?? []).map((r) => ({
          _id: nextRuleId(),
          table: r.table,
          column: r.column,
          operator: r.operator,
          value:
            r.operator === "BETWEEN"
              ? Array.isArray(r.value)
                ? r.value
                : [r.value ?? "", ""]
              : (r.value ?? ""),
          type: lookupColumnType(bootData.criterias, r.table, r.column),
          // TODO: Replace hardcoded list with `r.list` once backend provides it
          ...(r.column === "product_id" && {
            list: Array.isArray(r.list)
              ? r.list.map((v) => ({ id: v.value, name: v.name }))
              : [{ id: r.value, name: r.name }],
          }),
        }));
        dispatch({
          type: ACTION.SET_FORM_DATA,
          payload: {
            name: seg.name,
            description: seg.description ?? "",
            logical_operator: seg.logical_operator,
            rules,
          },
        });
        // Auto-run preview with the loaded rules
        if (rules.length > 0) {
          await runSearch(rules, seg.logical_operator);
        }
      }
    } catch (err) {
      errorMessage(
        err?.response?.data?.message ?? "Failed to load segment details.",
      );
    } finally {
      dispatch({ type: ACTION.SET_DETAIL_LOADING, payload: false });
    }
  };

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const init = async () => {
      const bootRes = await loadBootAndReturn();
      if (isEditMode && bootRes) {
        await loadSegment(id, bootRes);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Seed product options from editingRule.list when editing a product_id rule
  useEffect(() => {
    if (state.editingRuleIndex === null) return;
    const rule = state.formData.rules[state.editingRuleIndex];
    if (!rule || rule.column !== "product_id" || !Array.isArray(rule.list))
      return;

    // Seed productState.options with list items (dedup by id)
    setProductState((draft) => {
      const merged = [...draft.options, ...rule.list];
      const unique = merged.filter(
        (item, index, arr) => index === arr.findIndex((i) => i.id === item.id),
      );
      console.log(
        "Seeding product options from editing rule list:",
        merged,
        unique,
      ); // Debug log --- IGNORE ---
      draft.options = unique;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.editingRuleIndex]);

  // Internal: load boot and return data so edit-mode can use it immediately
  const loadBootAndReturn = async () => {
    dispatch({ type: ACTION.SET_BOOT_LOADING, payload: true });
    try {
      const res = await getSegmentationBoot();
      if (res.success) {
        dispatch({ type: ACTION.SET_BOOT_DATA, payload: res.data });
        return res.data;
      }
      return null;
    } catch (err) {
      errorMessage(
        err?.response?.data?.message ?? "Failed to load segmentation options.",
      );
      return null;
    } finally {
      dispatch({ type: ACTION.SET_BOOT_LOADING, payload: false });
    }
  };

  // ---------------------------------------------------------------------------
  // Form handlers
  // ---------------------------------------------------------------------------

  const onChangeField = (e) => {
    const { name, value } = e.target;
    dispatch({ type: ACTION.SET_FIELD, payload: { name, value } });
  };

  const toggleLogicalOperator = (value) => {
    if (value !== null) {
      dispatch({ type: ACTION.SET_LOGICAL_OPERATOR, payload: value });
    }
  };

  // ---------------------------------------------------------------------------
  // Criteria dialog
  // ---------------------------------------------------------------------------

  const openCriteriaDialog = () =>
    dispatch({ type: ACTION.SET_DIALOG_OPEN, payload: true });

  const closeCriteriaDialog = () => {
    dispatch({ type: ACTION.SET_DIALOG_OPEN, payload: false });
    dispatch({ type: ACTION.CLEAR_EDITING_RULE });
  };

  /** Called by dialog to add a new rule or update an existing one */
  const onAddOrUpdateRule = ({
    table,
    column,
    type,
    operator,
    value,
    list,
  }) => {
    if (state.editingRuleIndex !== null) {
      dispatch({
        type: ACTION.REPLACE_RULE,
        payload: { index: state.editingRuleIndex, operator, value, list },
      });
    } else {
      dispatch({
        type: ACTION.ADD_RULE,
        payload: {
          _id: nextRuleId(),
          table,
          column,
          operator,
          value,
          list,
          type,
        },
      });
    }
    closeCriteriaDialog();
  };
  /** Open dialog in edit mode for an existing rule */
  const onEditRule = (index) => {
    dispatch({ type: ACTION.SET_EDITING_RULE, payload: index });
  };

  // ---------------------------------------------------------------------------
  // Criteria rule updates
  // ---------------------------------------------------------------------------

  const onRemoveRule = (index) => {
    dispatch({ type: ACTION.REMOVE_RULE, payload: index });
  };

  // ---------------------------------------------------------------------------
  // Customer preview (Search)
  // ---------------------------------------------------------------------------

  const runSearch = async (
    rules,
    logicalOperator,
    pageIndex = DEFAULT_PAGINATION.pageIndex,
    pageSize = DEFAULT_PAGINATION.pageSize,
  ) => {
    dispatch({ type: ACTION.SET_PREVIEW_LOADING, payload: true });
    try {
      const payload = {
        logical_operator: logicalOperator,
        rules: formatRulesForApi(rules),
      };
      const res = await searchSegmentCustomers(payload, {
        page: pageIndex,
        per_page: pageSize,
      });
      if (res.success) {
        dispatch({
          type: ACTION.SET_PREVIEW,
          payload: {
            customers: res.data.customers ?? [],
            pagination: res.data.pagination ?? DEFAULT_PAGINATION,
          },
        });
      } else {
        errorMessage(res.message ?? "Search failed.");
      }
    } catch (err) {
      errorMessage(err?.response?.data?.message ?? "Customer search failed.");
    } finally {
      dispatch({ type: ACTION.SET_PREVIEW_LOADING, payload: false });
    }
  };

  const onSearchPreview = (
    pageIndex = DEFAULT_PAGINATION.pageIndex,
    pageSize = DEFAULT_PAGINATION.pageSize,
  ) => {
    if (state.formData.rules.length === 0) {
      errorMessage("Please add at least one criteria before searching.");
      return;
    }
    runSearch(
      state.formData.rules,
      state.formData.logical_operator,
      pageIndex,
      pageSize,
    );
  };

  const onPreviewGotoPage = (pageIndex) =>
    onSearchPreview(pageIndex, state.previewPagination.pageSize);

  const onPreviewSetPageSize = (e) => onSearchPreview(1, +e.target.value);

  // ---------------------------------------------------------------------------
  // Build API payload (strips _id / type from rules)
  // ---------------------------------------------------------------------------

  const buildApiPayload = () => ({
    name: state.formData.name,
    description: state.formData.description,
    logical_operator: state.formData.logical_operator,
    rules: formatRulesForApi(state.formData.rules),
  });

  // ---------------------------------------------------------------------------
  // Submit (create / update)
  // ---------------------------------------------------------------------------

  const onSubmit = async () => {
    if (!validator.current.allValid()) {
      validator.current.showMessages();
      setForceUpdate((prev) => prev + 1);
      return;
    }
    if (state.formData.rules.length === 0) {
      errorMessage("Please add at least one criteria rule.");
      return;
    }

    dispatch({ type: ACTION.SET_SAVING, payload: true });
    try {
      const payload = buildApiPayload();
      if (isEditMode) {
        const res = await updateSegment(id, payload);
        if (res.success) {
          successMessage(res.message ?? "Segment updated successfully.");
          navigate(`/customer-segmentation/details/${id}`);
        } else {
          errorMessage(res.message ?? "Failed to update segment.");
        }
      } else {
        const res = await createSegment(payload);
        if (res.success) {
          successMessage(res.message ?? "Segment created successfully.");
          navigate("/customer-segmentation");
        } else {
          errorMessage(res.message ?? "Failed to create segment.");
        }
      }
    } catch (err) {
      errorMessage(
        err?.response?.data?.message ??
          (isEditMode
            ? "Failed to update segment."
            : "Failed to create segment."),
      );
    } finally {
      dispatch({ type: ACTION.SET_SAVING, payload: false });
    }
  };

  // ---------------------------------------------------------------------------
  // Breadcrumbs
  // ---------------------------------------------------------------------------

  const title = isEditMode
    ? state.formData.name || "Edit Segment"
    : "New Segment";

  const breadcrumbLinks = React.useMemo(
    () => [
      { name: "Dashboard", href: "/" },
      { name: "Customer Segments", href: "/customer-segmentation" },
      ...(isEditMode
        ? [{ name: title, href: `/customer-segmentation/details/${id}` }]
        : []),
      { name: isEditMode ? "Edit" : "Create" },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [title, isEditMode, id],
  );

  return {
    state,
    isEditMode,
    title,
    validator,
    breadcrumbLinks,
    dropdownOptions,
    asyncDropdownOptions,
    onChangeField,
    toggleLogicalOperator,
    openCriteriaDialog,
    closeCriteriaDialog,
    onAddOrUpdateRule,
    onEditRule,
    onRemoveRule,
    onSearchPreview,
    onPreviewGotoPage,
    onPreviewSetPageSize,
    onSubmit,
  };
};
